/**
 * AoE Geometry Engine — pure functions for D&D 5e Area of Effect shapes.
 * Used by both server (getAffectedTokens) and client (canvas render).
 * No side effects, no DOM/Canvas dependencies.
 */
/** D&D 5e cone: 53° total angle → 26.5° half-angle */
export const CONE_HALF_ANGLE = 26.5 * (Math.PI / 180);
/** Default line width in feet for D&D 5e line AoE */
const LINE_WIDTH_FT = 5;
/** Number of polygon segments for circle approximation */
const CIRCLE_SEGMENTS = 32;
// ── Conversion helpers ───────────────────────────────────────
function ftToPx(ft, gridSize, gridSizeFt) {
    return (ft / gridSizeFt) * gridSize;
}
// ── isPointInAoe ─────────────────────────────────────────────
/**
 * Test whether a point (px, py) in canvas pixels falls within an AoE template.
 */
export function isPointInAoe(px, py, template, gridSize, gridSizeFt) {
    const { shape, originX, originY, rotation, sizeFt } = template;
    const sizePx = ftToPx(sizeFt, gridSize, gridSizeFt);
    switch (shape) {
        case 'sphere':
        case 'cylinder': {
            // Circle: origin = center, sizeFt = radius
            const dx = px - originX;
            const dy = py - originY;
            return dx * dx + dy * dy <= sizePx * sizePx;
        }
        case 'cone': {
            // Cone: origin = apex, rotation = direction, 53° total angle, sizeFt = length
            const dx = px - originX;
            const dy = py - originY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > sizePx || dist === 0)
                return false;
            const rotRad = (rotation * Math.PI) / 180;
            const pointAngle = Math.atan2(dy, dx);
            let angleDiff = pointAngle - rotRad;
            // Normalize to [-PI, PI]
            while (angleDiff > Math.PI)
                angleDiff -= 2 * Math.PI;
            while (angleDiff < -Math.PI)
                angleDiff += 2 * Math.PI;
            return Math.abs(angleDiff) <= CONE_HALF_ANGLE;
        }
        case 'cube': {
            // Cube: origin = center of one face, sizeFt = side length
            // The cube extends from the origin face in the direction of rotation
            const rotRad = (rotation * Math.PI) / 180;
            const dx = px - originX;
            const dy = py - originY;
            // Transform to local coordinates (rotated)
            const cosR = Math.cos(-rotRad);
            const sinR = Math.sin(-rotRad);
            const localX = dx * cosR - dy * sinR;
            const localY = dx * sinR + dy * cosR;
            // In local space: cube extends from x=0 to x=sizePx, centered on Y axis
            const halfSide = sizePx / 2;
            return localX >= 0 && localX <= sizePx && localY >= -halfSide && localY <= halfSide;
        }
        case 'line': {
            // Line: origin = start, sizeFt = length, 5ft width
            const widthPx = ftToPx(LINE_WIDTH_FT, gridSize, gridSizeFt);
            const rotRad = (rotation * Math.PI) / 180;
            const dx = px - originX;
            const dy = py - originY;
            // Transform to local coordinates
            const cosR = Math.cos(-rotRad);
            const sinR = Math.sin(-rotRad);
            const localX = dx * cosR - dy * sinR;
            const localY = dx * sinR + dy * cosR;
            const halfWidth = widthPx / 2;
            return localX >= 0 && localX <= sizePx && localY >= -halfWidth && localY <= halfWidth;
        }
        default:
            return false;
    }
}
// ── getAoeBoundingBox ────────────────────────────────────────
export function getAoeBoundingBox(template, gridSize, gridSizeFt) {
    const polygon = getAoePolygon(template, gridSize, gridSizeFt);
    if (polygon.length === 0) {
        return { x: template.originX, y: template.originY, width: 0, height: 0 };
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of polygon) {
        if (p.x < minX)
            minX = p.x;
        if (p.y < minY)
            minY = p.y;
        if (p.x > maxX)
            maxX = p.x;
        if (p.y > maxY)
            maxY = p.y;
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
// ── getAoePolygon ────────────────────────────────────────────
/**
 * Returns polygon points for canvas path rendering.
 * For circles (sphere/cylinder), returns approximation with CIRCLE_SEGMENTS points.
 */
export function getAoePolygon(template, gridSize, gridSizeFt) {
    const { shape, originX, originY, rotation, sizeFt } = template;
    const sizePx = ftToPx(sizeFt, gridSize, gridSizeFt);
    switch (shape) {
        case 'sphere':
        case 'cylinder': {
            const points = [];
            for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
                const angle = (2 * Math.PI * i) / CIRCLE_SEGMENTS;
                points.push({
                    x: originX + Math.cos(angle) * sizePx,
                    y: originY + Math.sin(angle) * sizePx,
                });
            }
            return points;
        }
        case 'cone': {
            const rotRad = (rotation * Math.PI) / 180;
            const startAngle = rotRad - CONE_HALF_ANGLE;
            const endAngle = rotRad + CONE_HALF_ANGLE;
            const arcSegments = 16;
            const points = [{ x: originX, y: originY }];
            for (let i = 0; i <= arcSegments; i++) {
                const angle = startAngle + (endAngle - startAngle) * (i / arcSegments);
                points.push({
                    x: originX + Math.cos(angle) * sizePx,
                    y: originY + Math.sin(angle) * sizePx,
                });
            }
            return points;
        }
        case 'cube': {
            const rotRad = (rotation * Math.PI) / 180;
            const cosR = Math.cos(rotRad);
            const sinR = Math.sin(rotRad);
            const halfSide = sizePx / 2;
            // 4 corners in local space: (0, -h), (s, -h), (s, h), (0, h)
            const localCorners = [
                { x: 0, y: -halfSide },
                { x: sizePx, y: -halfSide },
                { x: sizePx, y: halfSide },
                { x: 0, y: halfSide },
            ];
            return localCorners.map(c => ({
                x: originX + c.x * cosR - c.y * sinR,
                y: originY + c.x * sinR + c.y * cosR,
            }));
        }
        case 'line': {
            const widthPx = ftToPx(LINE_WIDTH_FT, gridSize, gridSizeFt);
            const rotRad = (rotation * Math.PI) / 180;
            const cosR = Math.cos(rotRad);
            const sinR = Math.sin(rotRad);
            const halfWidth = widthPx / 2;
            const localCorners = [
                { x: 0, y: -halfWidth },
                { x: sizePx, y: -halfWidth },
                { x: sizePx, y: halfWidth },
                { x: 0, y: halfWidth },
            ];
            return localCorners.map(c => ({
                x: originX + c.x * cosR - c.y * sinR,
                y: originY + c.x * sinR + c.y * cosR,
            }));
        }
        default:
            return [];
    }
}
// ── getAffectedTokens ────────────────────────────────────────
/**
 * Returns IDs of tokens whose center falls within the AoE area.
 * Center calculation accounts for token size (1x1 = Medium, 2x2+ = Large+).
 */
export function getAffectedTokens(template, tokens, gridSize, gridSizeFt) {
    const affected = [];
    for (const token of tokens) {
        const centerX = token.x + (token.size * gridSize) / 2;
        const centerY = token.y + (token.size * gridSize) / 2;
        if (isPointInAoe(centerX, centerY, template, gridSize, gridSizeFt)) {
            affected.push(token.id);
        }
    }
    return affected;
}
